import { useCallback, useMemo, useState } from 'react';
import type { MediaWidgetSource } from 'fym-shared/src/smartlinks/mediaWidget';
import { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import { useGetCatalogQuery } from '../../../../../../redux/api/fymAPI';

interface UseMediaBlockSongProps {
  accountId: number;
  smartLinkId: number;
  /** The saved source of the block, if any. */
  source: MediaWidgetSource | null;
}

/**
 * The catalog song of a SmartLink page: the row whose SmartLinks include this page
 * (`smartLinkIds`: every page of the same track and the pre-save page), else the row whose first
 * page it is (`smartLinkId`, an older answer). Undefined for a page with no song (the bio link).
 */
export function songOfSmartLink(items: CatalogItem[], smartLinkId: number): string | undefined {
  return (
    items.find((item) => item.smartLinkIds?.includes(smartLinkId) === true)?.uuid ??
    items.find((item) => item.smartLinkId === smartLinkId)?.uuid
  );
}

/**
 * The song filter of the rail (owner request 2026-10-09). The default is the song of this page. A
 * page with no song (the bio link) opens on the song of the saved file, else on "All songs" (`song`
 * undefined). The select changes it.
 */
export function useMediaBlockSong({ accountId, smartLinkId, source }: UseMediaBlockSongProps) {
  const catalog = useGetCatalogQuery({ accountId });
  const items = useMemo(() => catalog.data?.items ?? [], [catalog.data]);
  const [picked, setPicked] = useState<{ uuid: string | undefined } | null>(null);

  const pageSong = useMemo(() => songOfSmartLink(items, smartLinkId), [items, smartLinkId]);
  const savedSong = source !== null && source.kind !== 'upload' ? source.catalogItemUuid : undefined;
  const song = picked === null ? (pageSong ?? savedSong) : picked.uuid;

  const changeSong = useCallback((uuid: string | undefined) => setPicked({ uuid }), []);

  return { items, song, pageSong, changeSong, isLoading: catalog.isLoading };
}
